import { ChangeEvent, FocusEvent, useState } from 'react';
import cn from 'classnames';
import IconWrapper from '../Icons';
import styles from './NumberInput.module.scss';

export interface NumberInputProps {
  value: number | null;
  handleUpdate: (value: number | null) => void;
  minValue?: number;
  maxValue?: number;
  inputName: string;
  inputId: string;
  increaseAriaLabel: string;
  decreaseAriaLabel: string;
  inputAriaLabel: string;
  condensed?: boolean;
  minIncrement?: number | null;
}

export const NumberInput = ({
  value,
  handleUpdate,
  minValue = 1,
  maxValue = 999,
  inputName,
  inputId,
  increaseAriaLabel,
  decreaseAriaLabel,
  inputAriaLabel,
  condensed,
  minIncrement = null,
}: NumberInputProps) => {
  const [previousValue, setPreviousValue] = useState<number | null>(value);
  const [incrementValue, setIncrementValue] = useState<number | null>(
    minIncrement ?? null
  );

  const updateValue = (newValue: number | null) => {
    if (value !== null) setPreviousValue(value);
    handleUpdate(newValue);
  };

  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = ev.target.value.replace(/[^0-9]/, '').substr(0, 3);

    if (fieldValue.length) {
      let newValue = parseInt(fieldValue);

      if (newValue > maxValue) newValue = maxValue;
      if (newValue < minValue) newValue = minValue;

      updateValue(newValue);
    } else {
      updateValue(null);
    }
  };

  const onBlur = () => {
    if (value === null) updateValue(previousValue);
  };

  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const onIncrease = () => {
    if (value !== null) {
      const newValue = value < maxValue ? value + (incrementValue ?? 1) : value;
      updateValue(newValue);
    }
  };

  const onDecrease = () => {
    if (value !== null) {
      const newValue = value > minValue ? value - (incrementValue ?? 1) : value;
      updateValue(newValue);
    }
  };

  return (
    <div className={styles.numberInputRoot}>
      <button
        className={cn(styles.numberInputButton, {
          [styles.numberInputButtonCondensed]: condensed,
        })}
        onClick={onDecrease}
        type="button"
        aria-label={decreaseAriaLabel}
      >
        <IconWrapper
          classNames={styles.icon}
          icon="minus"
          width={24}
          height={24}
        />
      </button>

      <input
        type="text"
        name={inputName}
        id={inputId}
        className={cn(styles.numberInputField, {
          [styles.numberInputFieldCondensed]: condensed,
        })}
        min={minValue}
        max={maxValue}
        value={value ?? ''}
        pattern="[0-9]*"
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        aria-label={inputAriaLabel}
        disabled={minIncrement !== null}
      />

      <button
        className={cn(styles.numberInputButton, {
          [styles.numberInputButtonCondensed]: condensed,
        })}
        onClick={onIncrease}
        type="button"
        aria-label={increaseAriaLabel}
      >
        <IconWrapper
          classNames={styles.icon}
          icon="plus"
          width={24}
          height={24}
        />
      </button>
    </div>
  );
};

export default NumberInput;
